/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import get from 'lodash/get';
import {
    Icon,
    FauxUnavailableImage,
    StringHelper,
    Button,
    MobileCoverageRating,
    StarRating,
    FauxImage,
    HelpTip,
    InlineBulletList,
} from 'sarsaparilla';

import { FavoriteHeart } from 'shared-ui/favorites';

import { kmsToMiles } from '../../utils/utils';
import { SEARCH_FILTER_AGENCIES_VALUES } from '../../constants';
import { getTargetUri } from '../../utils/pathUtils';
import { searchDate } from '../../utils/dateUtils';
import { SearchResultCardLinkHeading } from './components/SearchResultCardLinkHeading';
import { CardSurfaceContext } from './CardSurfaceContext';
import {
    getPriceRange,
    CardInventoryTag,
    formatLocationName,
} from './SearchResultCard.utils';

const propTypes = {
    checkinTime: PropTypes.string,
    checkoutTime: PropTypes.string,
    dynamicClass: PropTypes.string,
    item: PropTypes.shape({
        average_rating_d: PropTypes.number,
        number_of_ratings_i: PropTypes.number,
        preview_image_url: PropTypes.string,
        name: PropTypes.string,
    }),
    onCardLeave: PropTypes.func,
    onCardOver: PropTypes.func,
    isCampingSearch: PropTypes.bool,
};

class Card extends React.Component {
    static propTypes = propTypes;

    static contextType = CardSurfaceContext;

    static displayName = 'Card';

    constructor(props) {
        super(props);

        this.onCardLeave = this.onCardLeave.bind(this);
        this.onCardOver = this.onCardOver.bind(this);
    }

    onCardLeave() {
        if (this.props.onCardLeave) {
            this.props.onCardLeave();
        }
    }

    onCardOver(item) {
        if (this.props.onCardOver) {
            this.props.onCardOver(item);
        }
    }

    getTargetUri(item) {
        if (item.entity_id) {
            return getTargetUri(item);
        }

        return null;
    }

    getAverageRating(item) {
        return Number.parseFloat(get(item, 'average_rating', 0.0));
    }

    getNumberOfRatings(item) {
        return Number.parseInt(get(item, 'number_of_ratings', 0), 10);
    }

    getDatesValue(item) {
        if (!this.props.isCampingSearch) {
            return '';
        }

        const availableDates = item?.available_dates?.[0];
        const startDateValue = availableDates?.start_date
            ? searchDate(availableDates?.start_date).utc()
            : null;
        const endDateValue = availableDates?.end_date
            ? searchDate(availableDates?.end_date).utc()
            : null;

        if (!startDateValue?.isValid() || !endDateValue?.isValid()) {
            return '';
        }

        return [startDateValue, endDateValue];
    }

    getDates(item) {
        if (!this.props.isCampingSearch) {
            return '';
        }

        const dates = this.getDatesValue(item);

        if (!dates) {
            return '';
        }

        const [startDateValue, endDateValue] = dates;
        const startDateMonth = startDateValue.format('MMM');
        const startDateDay = startDateValue.format('D');
        const endDateMonth = endDateValue.format('MMM');
        const endDateDay = endDateValue.format('D');

        if (startDateMonth === endDateMonth && startDateDay === endDateDay) {
            return `${startDateMonth} ${startDateDay}`;
        }

        if (startDateMonth === endDateMonth && startDateDay !== endDateDay) {
            return `${startDateMonth} ${startDateDay} - ${endDateDay}`;
        }

        return `${startDateMonth} ${startDateDay} - ${endDateMonth} ${endDateDay}`;
    }

    isTicketType(type) {
        return (
            type === 'ticketfacility' ||
            type === 'tour' ||
            type === 'timedentry' ||
            type === 'timedentry_tour'
        );
    }

    isCampingOrVenueType(type) {
        return type === 'campground' || type === 'venuereservations';
    }

    isTicketLottery(facilityId) {
        const facilityIds = [
            '250006',
            '250029',
            '10063832',
            '300008',
            '233374',
            '300010',
            '10088380',
            '10088311',
            '10090873',
            '10090620',
            '10112438', // Mammoth Cave National Park Floyd Collins History Tour Lottery
        ];
        return facilityIds.includes(facilityId);
    }

    renderParentOrg(item) {
        const parentURL = this.getTargetUri({
            entity_id: item.parent_id,
            entity_type: item.parent_type,
        });

        if (item.parent_name && item.parent_name.length > 0 && parentURL) {
            return <li translate="no">{formatLocationName(item.parent_name)}</li>;
        }
        if (item.parent_name && item.parent_name.length > 0) {
            return <li translate="no">{formatLocationName(item.parent_name)}</li>;
        }
        if (item.org_name && item.org_name.length > 0) {
            return <li translate="no">{formatLocationName(item.org_name)}</li>;
        }

        return null;
    }

    renderAvailability(item) {
        if (item.type === 'CAMPING_LOTTERY') {
            return <div className="rec-flex-card-lottery-info">Lottery</div>;
        }
        if (item.type === 'Activity Pass') {
            return <div className="rec-flex-card-available-info">Available</div>;
        }

        if (item.availability) {
            if (item.availability === 'partially available') {
                return (
                    <div className="rec-flex-card-available-info">
                        Partially Available
                    </div>
                );
            }
            if (item.availability === 'available') {
                return <div className="rec-flex-card-available-info">Available</div>;
            }
            if (item.availability === 'not reservable') {
                return (
                    <div className="rec-flex-card-firstcome-info">
                        First-come, First-served
                    </div>
                );
            }
            return (
                <div className="rec-flex-card-unavailable-info">
                    {StringHelper.toTitleCase(item.availability)}
                </div>
            );
        }
        if (
            (this.isTicketType(item.entity_type) &&
                !this.isTicketLottery(item.entity_id)) ||
            this.isCampingOrVenueType(item.entity_type)
        ) {
            if (!this.props.checkinTime && !this.props.checkoutTime) {
                return (
                    <div className="rec-flex-card-available-info">
                        ENTER DATES FOR AVAILABILITY
                    </div>
                );
            }
            return <div className="rec-flex-card-unavailable-info">UNAVAILABLE</div>;
        }
        return null;
    }

    renderLayout(item) {
        const { priceRangeLabel, priceRangeUnit } = getPriceRange(item);
        const averageRating = this.getAverageRating(item);
        const numberOfRatings = this.getNumberOfRatings(item);
        const cellCoverage = Number.parseFloat(
            get(item, 'aggregate_cell_coverage', -1.0)
        );

        const equipments = [];
        if (item.entity_type === 'campground' && item.campsite_equipment_name) {
            item.campsite_equipment_name.forEach((eqName, index) => {
                equipments[equipments.length] = (
                    <li key={`${index}_eq_name`}>{eqName}</li>
                );
            });
        }

        const favoriteHeart = (
            <FavoriteHeart
                entityId={item.entity_id}
                entityType={item.entity_type}
                parentId={item.parent_id}
                parentType={item.parent_type}
                favAriaLabel={formatLocationName(item.name)}
                showFavoriteText={false}
                editable
            />
        );

        const dates = this.getDates(item);

        let agencyCode = null;
        SEARCH_FILTER_AGENCIES_VALUES.forEach((agency) => {
            if (item.org_name === agency.value && item.entity_type !== 'recarea') {
                agencyCode = agency.key;
            }
        });

        return (
            <div
                className={`rec-flex-card-wrap ${this.props.dynamicClass} search-card-wrap`}
                id={`rec-card-${item.entity_id}_${item.entity_type}`}
                onBlur={this.onCardLeave.bind(this)}
                onFocus={this.onCardOver.bind(this, item)}
                onMouseOut={this.onCardLeave.bind(this)}
                onMouseOver={this.onCardOver.bind(this, item)}
                data-testid="search-card"
                data-ga-tracking-id="090142933987"
                data-ga-tracking-location={this.context ?? undefined}
            >
                <div data-card className="rec-flex-card-image-wrap">
                    {this.props.item.preview_image_url ? (
                        <FauxImage
                            src={this.props.item.preview_image_url}
                            altText={this.props.item.name} //TODO: pass in actual alt text describing image
                            className="rec-flex-card-image-wrap-faux-image"
                        />
                    ) : (
                        <FauxUnavailableImage
                            aspectRatio="4:3"
                            className="rec-flex-card-image-wrap-faux-image"
                        />
                    )}

                    {this.renderAvailability(item)}

                    {favoriteHeart}
                </div>

                <div className="rec-flex-card-content-wrap">
                    <div className="search-card-content-container">
                        <div className="rec-flex-card-content-top-wrap">
                            <div className="rec-flex-card-top-left-content-wrap">
                                <CardInventoryTag item={item} />
                            </div>
                            {dates && (
                                <div className="rec-flex-card-top-right-content-wrap">
                                    <div className="rec-flex-card-dates">
                                        <Icon iconName="calendar" /> {dates}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="rec-flex-card-body-wrap">
                            <SearchResultCardLinkHeading
                                item={item}
                                className="rec-flex-card-title"
                            />

                            <InlineBulletList className="text-sm">
                                {agencyCode && <li translate="no">{agencyCode}</li>}
                                {this.renderParentOrg(item)}
                                {item.city && item.state_code && (
                                    <li>
                                        Near {item.city}, {item.state_code}
                                    </li>
                                )}
                                {item.distance && item.distance.length > 0 && (
                                    <li>
                                        {item.distance < 1 && (
                                            <>
                                                {kmsToMiles(parseFloat(item.distance))}
                                                <span>miles away</span>
                                            </>
                                        )}
                                        {item.distance >= 1 && (
                                            <>
                                                {kmsToMiles(parseFloat(item.distance))}
                                                <span>miles</span>
                                                <span className="search-help-tip ml-fourth">
                                                    <HelpTip
                                                        tipForDescriptor="Point-to-point distance"
                                                        appearance="dashed"
                                                        triggerText="point-to-point"
                                                        content={`Point-to-point distance - also known as 'as the crow flies' -
                                            describes the straight-line distance from one location to another. It may be
                                             different from driving distance. Please verify actual distance based on your method of travel.`}
                                                    />
                                                </span>
                                            </>
                                        )}
                                    </li>
                                )}
                            </InlineBulletList>

                            <div className="rec-flex-card-lower-body-wrap">
                                <InlineBulletList className="text-sm mt-half">
                                    {item.campsite_accessible === 1 && (
                                        <li>
                                            <span className="search-accessible-button sarsa-button-icon-content">
                                                <span className="left-icon">
                                                    <Icon iconName="accessible" />
                                                </span>
                                            </span>
                                            {item.accessible_campsites_count} Accessible{' '}
                                            {item.accessible_campsites_count > 1
                                                ? 'Campsites'
                                                : 'Campsite'}
                                            <span className="search-help-tip">
                                                <HelpTip
                                                    tipForDescriptor="Accessible site"
                                                    content={`Accessible Site: This site has features that provide better accessibility than others.  Such features may include clear ground space, a firm surface and other features.  We are in the process of examining features that improve accessibility and welcome your feedback.`}
                                                />
                                            </span>
                                        </li>
                                    )}
                                </InlineBulletList>
                                <InlineBulletList className="text-sm">
                                    {equipments.length >= 1 && (
                                        <li className="rec-flex-card-meta-title">
                                            Allowed Equipment:
                                        </li>
                                    )}
                                    {equipments}
                                    {item.campgrounds_count && (
                                        <li>
                                            {item.campgrounds_count}{' '}
                                            {item.campgrounds_count <= 1
                                                ? 'Campground'
                                                : 'Campgrounds'}
                                        </li>
                                    )}
                                    {item.permits_count && (
                                        <li>
                                            {item.permits_count}{' '}
                                            {item.permits_count <= 1
                                                ? 'Permit'
                                                : 'Permits'}
                                        </li>
                                    )}
                                    {item.ticket_facilities_count && (
                                        <li>
                                            {item.ticket_facilities_count}{' '}
                                            {item.ticket_facilities_count <= 1
                                                ? 'Tour Facility'
                                                : 'Tour Facilities'}
                                        </li>
                                    )}
                                    {item.activitypass_count && (
                                        <li>
                                            {item.activitypass_count}{' '}
                                            {item.activitypass_count <= 1
                                                ? 'Activity Pass'
                                                : 'Activity Passes'}
                                        </li>
                                    )}
                                    {item.treepermits_count && (
                                        <li>
                                            {item.treepermits_count}{' '}
                                            {item.treepermits_count <= 1
                                                ? 'Tree Permit'
                                                : 'Tree Permits'}
                                        </li>
                                    )}
                                    {item.venuereservations_count && (
                                        <li>
                                            {item.venuereservations_count}{' '}
                                            {item.venuereservations_count <= 1
                                                ? 'Venue'
                                                : 'Venues'}
                                        </li>
                                    )}
                                </InlineBulletList>
                            </div>
                        </div>
                    </div>

                    {numberOfRatings > 0 ||
                    cellCoverage >= 0 ||
                    item.park_pass_facility_id ||
                    priceRangeLabel ? (
                        <div className="rec-flex-card-content-bottom-wrap">
                            <div className="ratings">
                                {numberOfRatings > 0 && (
                                    <div className="rating-info">
                                        <StarRating
                                            rating={averageRating}
                                            reviewCount={numberOfRatings}
                                        />
                                    </div>
                                )}

                                {cellCoverage >= 0 && (
                                    <div className="rating-info">
                                        <MobileCoverageRating
                                            rating={cellCoverage}
                                            hasDescription={false}
                                            hasLeftBorder
                                        />
                                    </div>
                                )}
                            </div>

                            {item.park_pass_facility_id && (
                                <Button
                                    size="xs"
                                    className="rec-flex-card-pass-button"
                                    href={`/sitepass/${item.park_pass_facility_id}`}
                                    aria-label={`Buy pass for ${item.name}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Buy a Pass
                                </Button>
                            )}

                            {priceRangeLabel ? (
                                <div className="rec-flex-card-price-button-wrap">
                                    <div className="rec-flex-card-price-wrap">
                                        {priceRangeLabel && (
                                            <span className="rec-flex-card-price">
                                                {priceRangeLabel}
                                            </span>
                                        )}
                                        {priceRangeUnit && (
                                            <span className="rec-flex-card-duration">
                                                {priceRangeUnit}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            </div>
        );
    }

    render() {
        const item = this.props.item;
        if (item !== undefined && item !== null) {
            return this.renderLayout(item);
        }
        return null;
    }
}

function select(state) {
    return {
        search: state.search,
    };
}

export default connect(select)(Card);

// cSpell:ignore checkin, ticketfacility, timedentry, recarea, vehiclepermit, venuereservations, activitypass, treepermit, firstcome, treepermits, sitepass, giftcard
